import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea, CardMedia, Grid, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, List, ListItem, ListItemText, Divider, CircularProgress, Alert,
  IconButton, ImageList, ImageListItem, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import {
  listEvents, createEvent, addPledge, addComment, uploadEventPhoto, deleteEvent,
} from '../api';

const TYPE_META = {
  WEDDING:     { label: 'Wedding',      emoji: '💒', color: '#065F46' },
  BURIAL:      { label: 'Burial',       emoji: '🕊️', color: '#059669' },
  DOWRY:       { label: 'Dowry',        emoji: '🐄', color: '#059669' },
  PROJECT:     { label: 'Project',      emoji: '🏗️', color: '#059669' },
  CELEBRATION: { label: 'Celebration',  emoji: '🎉', color: '#059669' },
  MEETING:     { label: 'Meeting',      emoji: '🗓️', color: '#059669' },
  OTHER:       { label: 'Event',        emoji: '📌', color: '#059669' },
};

const fmtKES = (n) => `KES ${Number(n || 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

const ADMIN_ROLES = ['ADMIN', 'PRESIDENT'];

const EventsPage = ({ currentUser }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // event open in detail dialog
  const [createOpen, setCreateOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listEvents();
      setEvents(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load events.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = async () => {
    const data = await load();
    if (selected) {
      const updated = data.find((e) => e._id === selected._id);
      if (updated) setSelected(updated);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Family Events</Typography>
          <Typography variant="body2" color="text.secondary">
            Weddings, burials, dowry, and family projects — planned and supported together.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)', whiteSpace: 'nowrap' }}
        >
          {isMobile ? 'New' : 'New event'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : events.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
          <Typography variant="h6">No events yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Be the first to post a family event.
          </Typography>
          <Button variant="outlined" onClick={() => setCreateOpen(true)}>Create the first event</Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {events.map((ev) => {
            const meta = TYPE_META[ev.type] || TYPE_META.OTHER;
            const pledged = (ev.pledges || []).reduce((s, p) => s + p.amount, 0);
            return (
              <Grid item xs={12} sm={6} key={ev._id}>
                <Card sx={{ borderRadius: 3, height: '100%' }}>
                  <CardActionArea onClick={() => setSelected(ev)} sx={{ height: '100%' }}>
                    {ev.coverPhoto && (
                      <CardMedia component="img" height="150" image={ev.coverPhoto} alt={ev.title} />
                    )}
                    <CardContent>
                      <Chip
                        label={`${meta.emoji} ${meta.label}`}
                        size="small"
                        sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 600, mb: 1 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{ev.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[fmtDate(ev.date), ev.location].filter(Boolean).join(' • ') || 'Details inside'}
                      </Typography>
                      {ev.targetAmount ? (
                        <Box sx={{ mt: 1.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (pledged / ev.targetAmount) * 100)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {fmtKES(pledged)} pledged of {fmtKES(ev.targetAmount)}
                          </Typography>
                        </Box>
                      ) : pledged > 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {fmtKES(pledged)} pledged so far
                        </Typography>
                      ) : null}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <CreateEventDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => { setCreateOpen(false); await load(); }}
      />

      {selected && (
        <EventDetailDialog
          event={selected}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={refreshSelected}
          onDeleted={async () => { setSelected(null); await load(); }}
        />
      )}
    </Box>
  );
};

/* ============================ Create dialog ============================ */

const CreateEventDialog = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setTitle(''); setType(''); setDate(''); setLocation('');
    setDescription(''); setTargetAmount(''); setError('');
  };

  const handleCreate = async () => {
    setError('');
    setSaving(true);
    try {
      await createEvent({ title, type, date, location, description, targetAmount });
      reset();
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New family event</DialogTitle>
      <DialogContent>
        <TextField label="Title" fullWidth margin="dense" value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Wedding of John & Mary" />
        <TextField select label="Type" fullWidth margin="dense" value={type}
          onChange={(e) => setType(e.target.value)}>
          {Object.entries(TYPE_META).map(([key, m]) => (
            <MenuItem key={key} value={key}>{m.emoji} {m.label}</MenuItem>
          ))}
        </TextField>
        <TextField label="Date" type="date" fullWidth margin="dense"
          InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
        <TextField label="Location" fullWidth margin="dense" value={location}
          onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Kitui" />
        <TextField label="Details" fullWidth margin="dense" multiline minRows={3}
          value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextField label="Contribution target in KES (optional)" type="number" fullWidth margin="dense"
          value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)}
          helperText="Set a goal if the family is raising funds for this event" />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !title.trim() || !type}
          onClick={handleCreate}
          sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}>
          {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Post event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ============================ Detail dialog ============================ */

const EventDetailDialog = ({ event, currentUser, onClose, onChanged, onDeleted }) => {
  const meta = TYPE_META[event.type] || TYPE_META.OTHER;
  const pledged = (event.pledges || []).reduce((s, p) => s + p.amount, 0);
  const [pledgeAmount, setPledgeAmount] = useState('');
  const [pledgeNote, setPledgeNote] = useState('');
  const [commentText, setCommentText] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const fileInput = useRef(null);

  const canManage =
    ADMIN_ROLES.includes(currentUser?.role) ||
    String(event.createdBy?.user) === String(currentUser?.id || currentUser?._id);

  const doAction = async (name, fn) => {
    setError('');
    setBusy(name);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const handlePledge = () => doAction('pledge', async () => {
    await addPledge(event._id, pledgeAmount, pledgeNote);
    setPledgeAmount(''); setPledgeNote('');
  });

  const handleComment = () => doAction('comment', async () => {
    await addComment(event._id, commentText);
    setCommentText('');
  });

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    doAction('photo', async () => { await uploadEventPhoto(event._id, file); });
    e.target.value = '';
  };

  const handleDelete = () => doAction('delete', async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    await deleteEvent(event._id);
    onDeleted();
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Chip label={`${meta.emoji} ${meta.label}`} size="small"
          sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 600, mr: 1 }} />
        {event.title}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {[fmtDate(event.date), event.location].filter(Boolean).join(' • ')}
          {event.createdBy?.name ? ` • Posted by ${event.createdBy.name}` : ''}
        </Typography>
        {event.description && <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{event.description}</Typography>}

        {(event.photos || []).length > 0 && (
          <ImageList cols={3} gap={6} sx={{ mb: 2 }}>
            {event.photos.map((url) => (
              <ImageListItem key={url}>
                <img src={url} alt="Event" loading="lazy" style={{ borderRadius: 8 }} />
              </ImageListItem>
            ))}
          </ImageList>
        )}
        <Button size="small" startIcon={<AddPhotoAlternateIcon />} disabled={busy === 'photo'}
          onClick={() => fileInput.current?.click()}>
          {busy === 'photo' ? 'Uploading…' : 'Add photo'}
        </Button>
        <input type="file" accept="image/*" hidden ref={fileInput} onChange={handlePhoto} />

        <Divider sx={{ my: 2 }} />

        {/* Contributions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VolunteerActivismIcon color="error" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Contributions</Typography>
        </Box>
        {event.targetAmount ? (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate"
              value={Math.min(100, (pledged / event.targetAmount) * 100)}
              sx={{ height: 10, borderRadius: 5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {fmtKES(pledged)} pledged of {fmtKES(event.targetAmount)} target
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">{fmtKES(pledged)} pledged so far</Typography>
        )}

        {(event.pledges || []).length > 0 && (
          <List dense sx={{ maxHeight: 160, overflowY: 'auto' }}>
            {event.pledges.slice().reverse().map((p, i) => (
              <ListItem key={i} disableGutters>
                <ListItemText
                  primary={`${p.name} — ${fmtKES(p.amount)}`}
                  secondary={p.note}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField label="Amount (KES)" type="number" size="small" value={pledgeAmount}
            onChange={(e) => setPledgeAmount(e.target.value)} sx={{ width: 140 }} />
          <TextField label="Note (optional)" size="small" fullWidth value={pledgeNote}
            onChange={(e) => setPledgeNote(e.target.value)} />
          <Button variant="contained" disabled={busy === 'pledge' || !pledgeAmount}
            onClick={handlePledge} sx={{ whiteSpace: 'nowrap' }}>
            Pledge
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Comments */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Comments</Typography>
        {(event.comments || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No comments yet — say something.</Typography>
        ) : (
          <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {event.comments.slice().reverse().map((c, i) => (
              <ListItem key={i} disableGutters alignItems="flex-start">
                <ListItemText
                  primary={c.name}
                  secondary={c.text}
                  primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField label="Write a comment" size="small" fullWidth value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) handleComment(); }} />
          <Button variant="contained" disabled={busy === 'comment' || !commentText.trim()} onClick={handleComment}>
            Post
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      {canManage && (
        <DialogActions>
          <Button color="error" disabled={busy === 'delete'} onClick={handleDelete}>
            Delete event
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EventsPage;
